import React, { useState, useEffect } from 'react';
import { Search, BookOpen, Heart, Target, Trophy, Check, Mail, Phone, MapPin, Sparkles, Globe, Users, Award, ArrowRight, TrendingUp, Zap, Star, Rocket, Brain, X, Instagram, Facebook, Linkedin } from 'lucide-react';

// Renamed from Homepage to App for single-file mandate
export default function App() {
  const [selectedCountry, setSelectedCountry] = useState('');
  const [customCountry, setCustomCountry] = useState('');
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [isVisible, setIsVisible] = useState({});
  const [scrollY, setScrollY] = useState(0);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false); // New state for message box

  const countries = [
    'India', 'USA', 'UK', 'Canada', 'Australia', 'Germany',
    'France', 'Singapore', 'New Zealand', 'Ireland', 'Other'
  ];

  const subjectOptions = ['Partnership', 'Admission Inquiry', 'Career Counseling'];

  const stats = [
    { number: '10,000+', label: 'Universities', icon: <Globe className="w-8 h-8" />, gradient: 'from-blue-500 to-cyan-500' },
    { number: '30+', label: 'Countries', icon: <Award className="w-8 h-8" />, gradient: 'from-purple-500 to-pink-500' },
    { number: '50,000+', label: 'Students Placed', icon: <Users className="w-8 h-8" />, gradient: 'from-orange-500 to-red-500' },
    { number: '100%', label: 'Commission Basis', icon: <TrendingUp className="w-8 h-8" />, gradient: 'from-green-500 to-emerald-500' }
  ];

  const domains = [
    { name: 'Technology & Engineering', icon: '💻', gradient: 'from-blue-500 to-cyan-500', specs: ['Computer Science & IT', 'Software Engineering', 'Data Science & Analytics', 'Artificial Intelligence'] },
    { name: 'Business & Management', icon: '💼', gradient: 'from-purple-500 to-pink-500', specs: ['Business Administration (MBA)', 'Finance & Banking', 'Marketing & Sales', 'Human Resources'] },
    { name: 'Healthcare & Medicine', icon: '⚕️', gradient: 'from-green-500 to-emerald-500', specs: ['Medicine (MBBS)', 'Nursing', 'Pharmacy', 'Dentistry'] },
    { name: 'Arts & Design', icon: '🎨', gradient: 'from-orange-500 to-red-500', specs: ['Fine Arts', 'Graphic Design', 'Fashion Design', 'Interior Design'] },
    { name: 'Science & Research', icon: '🔬', gradient: 'from-indigo-500 to-blue-500', specs: ['Physics', 'Chemistry', 'Biology', 'Mathematics'] },
    { name: 'Social Sciences & Humanities', icon: '📚', gradient: 'from-yellow-500 to-orange-500', specs: ['Psychology', 'Sociology', 'Political Science', 'International Relations'] },
    { name: 'Education & Teaching', icon: '👨‍🏫', gradient: 'from-teal-500 to-green-500', specs: ['Education (B.Ed/M.Ed)', 'Early Childhood Education', 'Special Education', 'Educational Technology'] },
    { name: 'Law & Legal Studies', icon: '⚖️', gradient: 'from-gray-600 to-gray-800', specs: ['Law (LLB/LLM)', 'Corporate Law', 'Criminal Law', 'Constitutional Law'] },
    { name: 'Agriculture & Food Sciences', icon: '🌾', gradient: 'from-lime-500 to-green-600', specs: ['Agriculture', 'Horticulture', 'Animal Science', 'Veterinary Science'] },
    { name: 'Hospitality & Tourism', icon: '🏨', gradient: 'from-rose-500 to-pink-600', specs: ['Hotel Management', 'Tourism Management', 'Event Management', 'Culinary Arts'] },
    { name: 'Music & Performing Arts', icon: '🎵', gradient: 'from-violet-500 to-purple-600', specs: ['Music Performance', 'Music Composition', 'Music Theory', 'Music Production'] },
    { name: 'Sports & Physical Education', icon: '⚽', gradient: 'from-amber-500 to-orange-600', specs: ['Physical Education', 'Sports Science', 'Kinesiology', 'Exercise Physiology'] }
  ];

  const services = [
    { title: 'Admissions', icon: <BookOpen className="w-10 h-10" />, gradient: 'from-blue-500 to-indigo-600', items: ['Regular Admission India & Abroad', 'Distance/Correspondence Learning', 'Online Certification'] },
    { title: 'Counselling', icon: <Heart className="w-10 h-10" />, gradient: 'from-pink-500 to-rose-600', items: ['Career Counselling', 'Mental Health Counselling', 'Stream/Subject Counselling'] },
    { title: 'Coaching & Preparation', icon: <Target className="w-10 h-10" />, gradient: 'from-purple-500 to-violet-600', items: ['IIT-JEE, NEET, NDA', 'CAT, CUET, CLAT', 'GMAT, TOEFL, SAT'] },
    { title: 'Training & Certification', icon: <Trophy className="w-10 h-10" />, gradient: 'from-orange-500 to-amber-600', items: ['NEP-Based Courses', 'Industry Certifications', 'Professional Skills'] }
  ];

  const features = [
    { title: 'AI-Powered Platform', desc: 'Advanced AI algorithms to match students with perfect educational opportunities', icon: <Brain className="w-8 h-8" />, gradient: 'from-purple-500 to-pink-500' },
    { title: 'Marketing Analytics', desc: 'Comprehensive marketing management with integrated social media capabilities', icon: <TrendingUp className="w-8 h-8" />, gradient: 'from-blue-500 to-cyan-500' },
    { title: 'Lead Management', desc: 'Automated lead management system to streamline student recruitment', icon: <Users className="w-8 h-8" />, gradient: 'from-green-500 to-emerald-500' },
    { title: 'Workzon Integration', desc: 'Built-in workpad for efficient task management and seamless workflow', icon: <Zap className="w-8 h-8" />, gradient: 'from-yellow-500 to-orange-500' },
    { title: 'Colabzon Collaboration', desc: 'In-built Slack-like capabilities for team collaboration', icon: <Globe className="w-8 h-8" />, gradient: 'from-indigo-500 to-purple-500' },
    { title: 'Performance Tracking', desc: 'Advanced KPI tracking, team targets, and incentive management', icon: <Award className="w-8 h-8" />, gradient: 'from-red-500 to-pink-500' }
  ];

  const testimonials = [
    { name: 'Priya Sharma', role: 'MBA Student, University of Toronto', text: 'Vidyazon made my dream of studying abroad a reality. Their AI-powered matching helped me find the perfect program, and the counseling team supported me throughout the entire process.', gradient: 'from-blue-500 to-cyan-500' },
    { name: 'Rajesh Kumar', role: 'Engineering Student, IIT Delhi', text: 'The coaching and preparation services at Vidyazon are exceptional. Their personalized approach and expert guidance helped me crack JEE with flying colors.', gradient: 'from-purple-500 to-pink-500' },
    { name: 'Anitha Reddy', role: 'Career Counselor, Hyderabad', text: 'As a partner, Vidyazon\'s platform has transformed how we manage students and track performance. The automation features save us hours of work every day.', gradient: 'from-orange-500 to-red-500' }
  ];

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible((prev) => ({ ...prev, [entry.target.id]: true }));
          }
        });
      },
      { threshold: 0.1 }
    );

    document.querySelectorAll('[id^="animate-"]').forEach((el) => {
      observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  const handleCountryChange = (e) => {
    setSelectedCountry(e.target.value);
    if (e.target.value !== 'Other') {
      setCustomCountry('');
    }
  };

  const handleFormChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log('Form Submitted:', formData, 'Country:', selectedCountry === 'Other' ? customCountry : selectedCountry);
    // Using a custom message box instead of the forbidden alert()
    setShowSuccessMessage(true);
    // Clear form data
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      subject: '',
      message: ''
    });
    setSelectedCountry('');
    setCustomCountry('');
    // Hide message after 5 seconds
    setTimeout(() => setShowSuccessMessage(false), 5000);
  };

  return (
    <div className="min-h-screen bg-white overflow-hidden font-sans">
      <style>
        {`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@100..900&display=swap');
        
        .font-sans {
          font-family: 'Inter', sans-serif;
        }

        @keyframes pulse-slow {
          0%, 100% { transform: scale(1); opacity: 0.3; }
          50% { transform: scale(1.1); opacity: 0.5; }
        }
        .animate-pulse-slow {
          animation: pulse-slow 8s infinite ease-in-out;
        }

        @keyframes float {
          0%, 100% { transform: translateY(0) rotate(0); }
          50% { transform: translateY(-20px) rotate(5deg); }
        }
        .animate-float {
          animation: float 10s infinite ease-in-out;
        }

        @keyframes fade-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        .animate-fade-in {
          animation: fade-in 0.5s ease-out forwards;
        }

        @keyframes slide-up {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-slide-up {
          animation: slide-up 0.6s ease-out forwards;
        }

        @keyframes fade-in-up {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in-up {
          animation: fade-in-up 0.7s ease-out forwards;
        }

        @keyframes gradient-x {
          0% { background-position: 0% 50%; }
          100% { background-position: 100% 50%; }
        }
        .animate-gradient-x {
          background-size: 200% 200%;
          animation: gradient-x 3s ease infinite alternate;
        }

        @keyframes scale-in {
          from { opacity: 0; transform: scale(0.9); }
          to { opacity: 1; transform: scale(1); }
        }
        .animate-scale-in {
          animation: scale-in 0.3s ease-out forwards;
        }

        .animation-delay-2000 { animation-delay: 2s; }
        .animation-delay-4000 { animation-delay: 4s; }
        .animation-delay-300 { animation-delay: 0.3s; }
        .animation-delay-600 { animation-delay: 0.6s; }

        @keyframes bounce-slow {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-5px); }
        }
        .animate-bounce-slow {
          animation: bounce-slow 4s infinite ease-in-out;
        }
        `}
      </style>

      {/* Success Message Modal */}
      {showSuccessMessage && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-30 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-sm w-full text-center border-t-8 border-green-500 animate-scale-in">
            <Check className="w-12 h-12 text-green-500 mx-auto mb-4 animate-bounce" />
            <h4 className="text-xl font-bold mb-2 text-gray-800">Message Sent!</h4>
            <p className="text-gray-600">Thank you for reaching out. We will connect with you soon.</p>
            <button
              onClick={() => setShowSuccessMessage(false)}
              className="mt-6 bg-green-500 hover:bg-green-600 text-white font-semibold py-2 px-6 rounded-xl transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center px-4 py-20">
        {/* Animated Decorative Elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-20 -left-20 w-96 h-96 bg-gradient-to-br from-blue-100 to-purple-100 rounded-full blur-3xl opacity-30 animate-pulse-slow"></div>
          <div className="absolute bottom-20 -right-20 w-96 h-96 bg-gradient-to-br from-pink-100 to-orange-100 rounded-full blur-3xl opacity-30 animate-pulse-slow animation-delay-2000"></div>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-gradient-to-br from-cyan-100 to-blue-100 rounded-full blur-3xl opacity-20 animate-pulse-slow animation-delay-4000"></div>
        </div>

        {/* Floating Elements */}
        {[...Array(15)].map((_, i) => (
          <div
            key={i}
            className="absolute"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animation: `float ${5 + Math.random() * 10}s ease-in-out infinite`,
              animationDelay: `${Math.random() * 5}s`
            }}
          >
            <div className={`w-2 h-2 bg-gradient-to-br ${stats[Math.floor(Math.random() * stats.length)].gradient} rounded-full opacity-40`}></div>
          </div>
        ))}

        <div className="relative z-10 max-w-7xl mx-auto w-full">
          <div className="text-center mb-12">
            {/* Badge */}
            <div className="inline-block mb-8 animate-fade-in">
              <div className="flex items-center gap-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white px-6 py-3 rounded-full shadow-lg hover:shadow-2xl transition-shadow group">
                <Sparkles className="w-5 h-5 animate-spin-slow" />
                <span className="font-bold">AI-Powered Education Platform</span>
                <Rocket className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </div>
            </div>

            {/* Main Heading */}
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black mb-6 animate-slide-up">
              <span className="block text-gray-900">Your Gateway to</span>
              <span className="block mt-2 bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 bg-clip-text text-transparent animate-gradient-x">
                Global Education
              </span>
            </h1>

            {/* Subheading */}
            <p className="text-xl sm:text-2xl text-gray-600 mb-12 max-w-4xl mx-auto animate-fade-in-up animation-delay-300">
              AI-powered platform connecting Indian students with <span className="font-bold text-blue-600">10,000+ universities</span> across <span className="font-bold text-purple-600">30+ countries</span>
            </p>

            {/* Search Bar */}
            <div className="max-w-5xl mx-auto animate-fade-in-up animation-delay-600">
              <div className="bg-white rounded-3xl shadow-2xl p-4 border-4 border-gray-100 hover:border-blue-200 transition-all duration-500">
                <div className="flex flex-col lg:flex-row gap-4">
                  <div className="flex-1 relative group">
                    <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-6 h-6 text-gray-400 group-focus-within:text-blue-500 transition-colors" />
                    <input
                      type="text"
                      placeholder="Search courses, universities..."
                      className="w-full pl-14 pr-4 py-4 rounded-2xl border-2 border-gray-200 text-gray-900 focus:outline-none focus:ring-4 focus:ring-blue-100 focus:border-blue-500 transition-all text-lg"
                    />
                  </div>
                  <div className="flex-1 relative">
                    <select
                      value={selectedCountry}
                      onChange={handleCountryChange}
                      className="w-full px-4 py-4 rounded-2xl border-2 border-gray-200 text-gray-900 focus:outline-none focus:ring-4 focus:ring-purple-100 focus:border-purple-500 appearance-none cursor-pointer transition-all text-lg font-medium"
                    >
                      <option value="">Select Country</option>
                      {countries.map((country) => (
                        <option key={country} value={country}>{country}</option>
                      ))}
                    </select>
                  </div>
                  {selectedCountry === 'Other' && (
                    <div className="flex-1 animate-scale-in">
                      <input
                        type="text"
                        placeholder="Enter country name..."
                        value={customCountry}
                        onChange={(e) => setCustomCountry(e.target.value)}
                        className="w-full px-4 py-4 rounded-2xl border-2 border-gray-200 text-gray-900 focus:outline-none focus:ring-4 focus:ring-green-100 focus:border-green-500 transition-all text-lg"
                      />
                    </div>
                  )}
                  <button className="group bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 text-white px-8 py-4 rounded-2xl font-bold hover:shadow-2xl transform hover:scale-105 transition-all duration-300 flex items-center justify-center gap-3 text-lg">
                    <Search className="w-6 h-6 group-hover:rotate-90 transition-transform duration-500" />
                    Search Now
                    <ArrowRight className="w-6 h-6 group-hover:translate-x-2 transition-transform duration-300" />
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 mt-24">
            {stats.map((stat, idx) => (
              <div
                key={idx}
                className={`group relative bg-white rounded-3xl p-8 border-2 border-gray-100 hover:border-transparent shadow-lg hover:shadow-2xl transform hover:scale-105 hover:-rotate-1 transition-all duration-500 cursor-pointer animate-fade-in-up`}
                style={{ animationDelay: `${900 + idx * 150}ms` }}
              >
                <div className={`absolute inset-0 bg-gradient-to-br ${stat.gradient} rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500`}></div>
                <div className="relative text-center">
                  <div className={`inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br ${stat.gradient} text-white mb-4 group-hover:scale-125 group-hover:rotate-12 transition-all duration-500`}>
                    {stat.icon}
                  </div>
                  <div className="text-4xl font-black bg-gradient-to-br from-gray-900 to-gray-600 bg-clip-text text-transparent group-hover:text-white mb-2 transition-all">
                    {stat.number}
                  </div>
                  <div className="text-gray-600 font-semibold group-hover:text-white transition-all">{stat.label}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="animate-services" className="relative py-24 px-4">
        <div className="max-w-7xl mx-auto">
          <div className={`text-center mb-20 transition-all duration-1000 ${isVisible['animate-services'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <div className="inline-block mb-6">
              <span className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-8 py-3 rounded-full text-sm font-bold shadow-lg">
                OUR SERVICES
              </span>
            </div>
            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black mb-6">
              <span className="bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent">
                Comprehensive Education Services
              </span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              From admissions to career guidance, we provide end-to-end support for your educational journey
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {services.map((service, idx) => (
              <div
                key={idx}
                className={`group relative bg-white rounded-3xl shadow-xl hover:shadow-2xl transform hover:-translate-y-4 transition-all duration-500 overflow-hidden border-2 border-gray-100 hover:border-transparent ${
                  isVisible['animate-services'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                }`}
                style={{ transitionDelay: `${idx * 100}ms` }}
              >
                <div className={`absolute inset-0 bg-gradient-to-br ${service.gradient} opacity-0 group-hover:opacity-100 transition-opacity duration-500`}></div>
                <div className="relative p-8">
                  <div className={`inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-gradient-to-br ${service.gradient} text-white mb-6 group-hover:scale-125 group-hover:rotate-12 transition-all duration-500 shadow-lg`}>
                    {service.icon}
                  </div>
                  <h3 className="text-2xl font-black mb-6 text-gray-900 group-hover:text-white transition-colors">
                    {service.title}
                  </h3>
                  <ul className="space-y-4">
                    {service.items.map((item, i) => (
                      <li key={i} className="flex items-start gap-3 text-gray-700 group-hover:text-white transition-colors">
                        <div className="mt-1 flex-shrink-0">
                          <Check className="w-5 h-5 text-green-500 group-hover:text-white group-hover:scale-125 transition-all" />
                        </div>
                        <span className="text-sm font-medium leading-relaxed">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className={`absolute bottom-0 left-0 right-0 h-2 bg-gradient-to-r ${service.gradient}`}></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Academic Domains */}
      <section id="animate-domains" className="relative py-24 px-4 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="max-w-7xl mx-auto">
          <div className={`text-center mb-20 transition-all duration-1000 ${isVisible['animate-domains'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <div className="inline-block mb-6">
              <span className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-8 py-3 rounded-full text-sm font-bold shadow-lg">
                200+ SPECIALIZATIONS
              </span>
            </div>
            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black mb-6">
              <span className="bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent">
                Academic Domains We Cover
              </span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Explore 12 major academic domains with access to 10,000+ universities worldwide
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {domains.map((domain, idx) => (
              <div
                key={idx}
                className={`group relative bg-white rounded-3xl shadow-xl hover:shadow-2xl transform hover:-translate-y-3 transition-all duration-500 overflow-hidden border-2 border-gray-100 ${
                  isVisible['animate-domains'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                }`}
                style={{ transitionDelay: `${idx * 80}ms` }}
              >
                <div className={`absolute top-0 left-0 right-0 h-3 bg-gradient-to-r ${domain.gradient}`}></div>
                <div className="p-8">
                  <div className="text-6xl mb-6 group-hover:scale-125 group-hover:rotate-12 transition-transform duration-500 inline-block">
                    {domain.icon}
                  </div>
                  <h3 className="text-2xl font-black mb-3 text-gray-900 group-hover:bg-gradient-to-r group-hover:bg-clip-text group-hover:text-transparent" style={{ backgroundImage: `linear-gradient(to right, var(--tw-gradient-stops))` }}>
                    {domain.name}
                  </h3>
                  <div className="flex items-center gap-2 mb-6">
                    <Award className="w-5 h-5 text-yellow-500" />
                    <p className="text-sm font-bold text-gray-500">
                      18 specializations available
                    </p>
                  </div>
                  <ul className="space-y-3">
                    {domain.specs.map((spec, i) => (
                      <li key={i} className="flex items-start gap-3 text-gray-700">
                        <span className={`mt-1 text-lg bg-gradient-to-br ${domain.gradient} bg-clip-text text-transparent font-bold`}>•</span>
                        <span className="text-sm font-medium leading-relaxed">{spec}</span>
                      </li>
                    ))}
                    <li className="flex items-center gap-2 text-sm font-bold">
                      <span className={`bg-gradient-to-r ${domain.gradient} bg-clip-text text-transparent`}>
                        +14 more specializations
                      </span>
                      <ArrowRight className="w-4 h-4 text-blue-600" />
                    </li>
                  </ul>
                </div>
              </div>
            ))}
          </div>

          <div className={`mt-20 relative overflow-hidden rounded-3xl shadow-2xl transition-all duration-1000 ${isVisible['animate-domains'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <div className="absolute inset-0 bg-gradient-to-br from-blue-600 via-purple-600 to-pink-600"></div>
            <div className="relative p-12 sm:p-16 text-center text-white">
              <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-white/20 backdrop-blur-md mb-8 animate-bounce-slow">
                <Sparkles className="w-10 h-10 animate-spin-slow" />
              </div>
              <h3 className="text-3xl sm:text-4xl font-black mb-6">Can't Find Your Domain?</h3>
              <p className="text-xl mb-10 max-w-2xl mx-auto text-white/90 leading-relaxed">
                With access to 10,000+ universities across 30+ countries, we cover virtually every academic field
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button className="group bg-white text-blue-600 px-10 py-5 rounded-2xl font-black hover:shadow-2xl transform hover:scale-105 transition-all flex items-center justify-center gap-3 text-lg">
                  Explore All Programs
                  <ArrowRight className="w-6 h-6 group-hover:translate-x-2 transition-transform" />
                </button>
                <button className="group bg-white/20 backdrop-blur-md border-3 border-white text-white px-10 py-5 rounded-2xl font-black hover:bg-white/30 transition-all flex items-center justify-center gap-3 text-lg">
                  <Phone className="w-6 h-6 group-hover:rotate-12 transition-transform" />
                  Talk to Counselor
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section - Completion of the existing code block */}
      <section id="animate-features" className="relative py-24 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className={`text-center mb-20 transition-all duration-1000 ${isVisible['animate-features'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <div className="inline-block mb-6">
              <span className="bg-gradient-to-r from-orange-600 to-red-600 text-white px-8 py-3 rounded-full text-sm font-bold shadow-lg">
                PLATFORM FEATURES
              </span>
            </div>
            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black mb-6">
              <span className="bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent">
                Powerful Platform Features
              </span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Built by industry experts with 20+ years of experience
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, idx) => (
              <div
                key={idx}
                className={`group relative bg-white rounded-3xl shadow-xl hover:shadow-2xl p-10 transform hover:-translate-y-3 transition-all duration-500 border-2 border-gray-100 hover:border-transparent overflow-hidden ${
                  isVisible['animate-features'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                }`}
                style={{ transitionDelay: `${idx * 100}ms` }}
              >
                <div className={`absolute top-0 right-0 w-40 h-40 bg-gradient-to-br ${feature.gradient} opacity-10 rounded-bl-full transform group-hover:scale-150 transition-transform duration-500`}></div>
                <div className="relative">
                  <div className={`inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br ${feature.gradient} text-white mb-6 group-hover:rotate-12 group-hover:scale-125 transition-all duration-500 shadow-lg`}>
                    {feature.icon}
                  </div>
                  <h3 className={`text-2xl font-black mb-4 bg-gradient-to-r ${feature.gradient} bg-clip-text text-transparent`}>
                    {feature.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed font-medium">{feature.desc}</p>
                </div>
              </div>
            ))}
          </div>

          {/* CTA Banner - This completes the incomplete block */}
          <div className={`mt-20 bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-600 rounded-3xl p-12 sm:p-16 text-white relative overflow-hidden shadow-2xl transition-all duration-1000 ${isVisible['animate-features'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <div className="relative">
              <div className="flex items-center gap-4 mb-6">
                <Zap className="w-10 h-10 text-yellow-300 animate-pulse" />
                <h3 className="text-3xl sm:text-4xl font-extrabold">Ready to Partner?</h3>
              </div>
              <p className="text-xl mb-8 max-w-3xl text-white/90 leading-relaxed">
                Join our network of counselors and access our full suite of AI-powered tools for seamless student recruitment and management.
              </p>
              <button className="group bg-white text-indigo-600 px-10 py-5 rounded-2xl font-black hover:shadow-2xl transform hover:scale-105 transition-all flex items-center justify-center gap-3 text-lg">
                Start Partnership Application
                <ArrowRight className="w-6 h-6 group-hover:translate-x-2 transition-transform" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="animate-testimonials" className="relative py-24 px-4 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className={`text-center mb-20 transition-all duration-1000 ${isVisible['animate-testimonials'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <div className="inline-block mb-6">
              <span className="bg-gradient-to-r from-green-600 to-emerald-600 text-white px-8 py-3 rounded-full text-sm font-bold shadow-lg">
                TRUSTED BY MANY
              </span>
            </div>
            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black mb-6">
              <span className="bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent">
                What Our Students Say
              </span>
            </h2>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            {testimonials.map((testimonial, idx) => (
              <div
                key={idx}
                className={`group relative bg-white rounded-3xl shadow-xl p-8 border-t-8 ${testimonial.gradient.replace('from-', 'border-')} hover:shadow-2xl transform hover:scale-[1.02] transition-all duration-500 ${
                  isVisible['animate-testimonials'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                }`}
                style={{ transitionDelay: `${idx * 150}ms` }}
              >
                <div className={`text-5xl mb-4 text-gray-300 font-serif font-bold group-hover:text-gray-400 transition-colors`}>"</div>
                <p className="text-lg italic text-gray-700 mb-6 relative z-10">{testimonial.text}</p>
                <div className="flex items-center gap-4 border-t pt-4">
                  <div className="w-12 h-12 rounded-full bg-gray-200 flex items-center justify-center text-xl font-bold text-gray-700">
                    {testimonial.name.charAt(0)}
                  </div>
                  <div>
                    <p className="font-bold text-gray-900">{testimonial.name}</p>
                    <p className="text-sm text-gray-500">{testimonial.role}</p>
                  </div>
                </div>
                <div className="absolute top-8 right-8 flex gap-1">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="animate-contact" className="relative py-24 px-4 bg-white">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-16">
          <div className={`space-y-6 transition-all duration-1000 ${isVisible['animate-contact'] ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'}`}>
            <div className="inline-block mb-6">
              <span className="bg-gradient-to-r from-red-600 to-pink-600 text-white px-8 py-3 rounded-full text-sm font-bold shadow-lg">
                GET IN TOUCH
              </span>
            </div>
            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black mb-6">
              <span className="bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent">
                Ready to Start Your Journey?
              </span>
            </h2>
            <p className="text-xl text-gray-600 leading-relaxed">
              Fill out the form below or reach out to us directly. Our expert counselors are here to guide you every step of the way.
            </p>

            <div className="space-y-4 pt-6">
              <div className="flex items-center gap-4">
                <Mail className="w-6 h-6 text-blue-600" />
                <a href="mailto:info@vidyazon.com" className="text-gray-700 hover:text-blue-600 transition-colors font-medium">info@vidyazon.com</a>
              </div>
              <div className="flex items-center gap-4">
                <Phone className="w-6 h-6 text-purple-600" />
                <a href="tel:+919876543210" className="text-gray-700 hover:text-purple-600 transition-colors font-medium">+91 98765 43210 (24/7 Support)</a>
              </div>
              <div className="flex items-start gap-4">
                <MapPin className="w-6 h-6 text-pink-600 flex-shrink-0 mt-1" />
                <p className="text-gray-700 font-medium">Global Head Office, 123 Education Boulevard, New Delhi, India 110001</p>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <form
            onSubmit={handleSubmit}
            className={`bg-gray-50 p-8 sm:p-12 rounded-3xl shadow-2xl border-t-8 border-blue-600 transition-all duration-1000 ${isVisible['animate-contact'] ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'}`}
          >
            <h3 className="text-3xl font-black mb-8 text-gray-800">Send Us a Message</h3>
            <div className="grid sm:grid-cols-2 gap-6 mb-6">
              <input
                type="text"
                name="firstName"
                placeholder="First Name"
                value={formData.firstName}
                onChange={handleFormChange}
                required
                className="w-full px-5 py-4 border border-gray-300 rounded-xl focus:outline-none focus:ring-4 focus:ring-blue-100 focus:border-blue-500 transition-all"
              />
              <input
                type="text"
                name="lastName"
                placeholder="Last Name"
                value={formData.lastName}
                onChange={handleFormChange}
                required
                className="w-full px-5 py-4 border border-gray-300 rounded-xl focus:outline-none focus:ring-4 focus:ring-blue-100 focus:border-blue-500 transition-all"
              />
            </div>
            <div className="grid sm:grid-cols-2 gap-6 mb-6">
              <input
                type="email"
                name="email"
                placeholder="Email Address"
                value={formData.email}
                onChange={handleFormChange}
                required
                className="w-full px-5 py-4 border border-gray-300 rounded-xl focus:outline-none focus:ring-4 focus:ring-purple-100 focus:border-purple-500 transition-all"
              />
              <input
                type="tel"
                name="phone"
                placeholder="Phone Number"
                value={formData.phone}
                onChange={handleFormChange}
                required
                className="w-full px-5 py-4 border border-gray-300 rounded-xl focus:outline-none focus:ring-4 focus:ring-purple-100 focus:border-purple-500 transition-all"
              />
            </div>
            <div className="grid sm:grid-cols-2 gap-6 mb-6">
              <select
                name="subject"
                value={formData.subject}
                onChange={handleFormChange}
                required
                className="w-full px-5 py-4 border border-gray-300 rounded-xl focus:outline-none focus:ring-4 focus:ring-pink-100 focus:border-pink-500 appearance-none transition-all cursor-pointer"
              >
                <option value="" disabled>Select Subject</option>
                {subjectOptions.map(option => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
              <select
                value={selectedCountry}
                onChange={handleCountryChange}
                required
                className="w-full px-5 py-4 border border-gray-300 rounded-xl focus:outline-none focus:ring-4 focus:ring-pink-100 focus:border-pink-500 appearance-none transition-all cursor-pointer"
              >
                <option value="" disabled>Preferred Study Country</option>
                {countries.map((country) => (
                  <option key={country} value={country}>{country}</option>
                ))}
              </select>
            </div>
            {selectedCountry === 'Other' && (
              <input
                type="text"
                placeholder="Enter specific country name"
                value={customCountry}
                onChange={(e) => setCustomCountry(e.target.value)}
                className="w-full px-5 py-4 mb-6 border border-gray-300 rounded-xl focus:outline-none focus:ring-4 focus:ring-green-100 focus:border-green-500 transition-all"
              />
            )}
            <textarea
              name="message"
              placeholder="Your Message"
              value={formData.message}
              onChange={handleFormChange}
              rows="4"
              required
              className="w-full px-5 py-4 mb-8 border border-gray-300 rounded-xl focus:outline-none focus:ring-4 focus:ring-blue-100 focus:border-blue-500 transition-all"
            ></textarea>
            <button
              type="submit"
              className="group w-full bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 text-white px-8 py-5 rounded-2xl font-black hover:shadow-2xl transform hover:scale-[1.01] transition-all duration-300 flex items-center justify-center gap-3 text-lg"
            >
              Submit Inquiry
              <ArrowRight className="w-6 h-6 group-hover:translate-x-2 transition-transform duration-300" />
            </button>
          </form>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-16 px-4">
        <div className="max-w-7xl mx-auto grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-10">
          <div className="col-span-2 md:col-span-4 lg:col-span-2 space-y-4">
            <h4 className="text-3xl font-black bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">Vidyazon</h4>
            <p className="text-gray-400 max-w-sm">AI-powered platform connecting students with global education opportunities and comprehensive career support.</p>
            <div className="flex space-x-4 pt-4">
              <a href="#" className="p-3 rounded-full bg-gray-700 hover:bg-blue-600 transition-colors"><Facebook className="w-5 h-5" /></a>
              <a href="#" className="p-3 rounded-full bg-gray-700 hover:bg-pink-600 transition-colors"><Instagram className="w-5 h-5" /></a>
              <a href="#" className="p-3 rounded-full bg-gray-700 hover:bg-blue-800 transition-colors"><Linkedin className="w-5 h-5" /></a>
              <a href="#" className="p-3 rounded-full bg-gray-700 hover:bg-cyan-600 transition-colors"><X className="w-5 h-5" /></a>
            </div>
          </div>

          <div className="space-y-4">
            <h5 className="text-xl font-bold mb-3 text-blue-300">Quick Links</h5>
            <ul className="space-y-2 text-gray-400">
              <li><a href="#" className="hover:text-white transition-colors">About Us</a></li>
              <li><a href="#" className="hover:text-white transition-colors">Our Team</a></li>
              <li><a href="#" className="hover:text-white transition-colors">Careers</a></li>
              <li><a href="#" className="hover:text-white transition-colors">Blog</a></li>
            </ul>
          </div>

          <div className="space-y-4">
            <h5 className="text-xl font-bold mb-3 text-purple-300">Services</h5>
            <ul className="space-y-2 text-gray-400">
              <li><a href="#" className="hover:text-white transition-colors">Admissions Abroad</a></li>
              <li><a href="#" className="hover:text-white transition-colors">Career Counselling</a></li>
              <li><a href="#" className="hover:text-white transition-colors">Test Preparation</a></li>
              <li><a href="#" className="hover:text-white transition-colors">Training & Certs</a></li>
            </ul>
          </div>

          <div className="space-y-4">
            <h5 className="text-xl font-bold mb-3 text-pink-300">Support</h5>
            <ul className="space-y-2 text-gray-400">
              <li><a href="#" className="hover:text-white transition-colors">FAQ</a></li>
              <li><a href="#" className="hover:text-white transition-colors">Privacy Policy</a></li>
              <li><a href="#" className="hover:text-white transition-colors">Terms of Service</a></li>
              <li><a href="#" className="hover:text-white transition-colors">Contact Support</a></li>
            </ul>
          </div>
        </div>

        <div className="max-w-7xl mx-auto border-t border-gray-700 mt-12 pt-8 text-center">
          <p className="text-sm text-gray-500">&copy; {new Date().getFullYear()} Vidyazon. All rights reserved. Designed with ❤️ for Global Education.</p>
        </div>
      </footer>
    </div>
  );
}
